import type { ReactElement } from 'react'
import { uniqueId } from 'lodash-es'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { useState } from 'react'

import { deleteCustomer, logoutCustomer, setThemeNotification } from '../../../../store/actions'
import { generateShopUrl } from '../../../../urlGenerators'
import { useTranslate } from '../../../../utils/translate'
import { withQueryAndScope } from '../../../../urlGeneratorsPlain'
import Lightbox from '../../../templateComponents/Lightbox'
import Portal from '../../../templateComponents/Portal'

type Props = {
  show: boolean
  onClose: () => void
}

export default function AccountDeleteModal({ show, onClose }: Readonly<Props>): ReactElement {
  const t = useTranslate('shop')

  const scopedT: TranslateProps['t'] = (key, options) =>
    t(`views.storefrontView.customerAccountSection.settingsTab.deleteProfileSection.confirmationModal.${key}`, options)

  const dispatch = useDispatch()
  const apiDispatch = useDispatch<GlobalDispatch>()

  const [submitting, setSubmitting] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()

  async function onDelete() {
    setSubmitting(true)

    try {
      await apiDispatch(deleteCustomer())

      dispatch(
        setThemeNotification({
          id: uniqueId(`customer-deleted-success-`),
          message: <span>{t('notifications.customerProfileDeleted')}</span>,
          type: 'success',
        }),
      )

      await apiDispatch(logoutCustomer())

      navigate(withQueryAndScope(generateShopUrl(), location))
    } catch {
      // global API call error handler takes care
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Portal>
      <Lightbox show={show} onClose={onClose} lightboxClassName="lightbox-modal">
        {({ close }) => (
          <div className="lightbox-modal-form account-delete-form">
            <div className="inner-content">
              <h2 className="title">{scopedT('title')}</h2>
              <p className="description">{scopedT('description')}</p>
            </div>
            <div className="button-wrapper">
              <button
                type="submit"
                onClick={onDelete}
                className="button button-delete button-pending-state pending"
                disabled={submitting}
              >
                {scopedT('deleteButton.label')}
              </button>
              <button type="button" onClick={close} className="button button-outline">
                {scopedT('backButton.label')}
              </button>
            </div>
          </div>
        )}
      </Lightbox>
    </Portal>
  )
}
